import React, { useEffect, useRef, useState } from 'react'
import config from '../config'
import { get, post, updateDefaultHeader } from '../utils/http'
import { useNavigate } from 'react-router-dom'
import { useUserContext, UserResponse } from '../contexts/user-context'
import { AUTH_KEY, AUTH_TYPE_KEY, OAUTH_PATH_DATA_KEY } from '../constants/localStorageKeys'
import './Login.scss'
import Card from '../components/Card/Card'
import InputText from '../components/InputText/InputText'
import SpinnerIcon from '../components/Icons/SpinnerIcon'
import { AuthorizeResponse } from '../types/AuthorizeResponse'
import { TokenResponse } from '../types/TokenResponse'
import { IntrospectResponse } from '../types/IntrospectResponse'
import { getEntraIdUserFromToken } from '../utils/getEntraIdUserFromToken'
import { AuthType } from '../constants/authTypes'
import { ENTRA_ID_CLIENT } from '../constants/httpHeaderValues'
import { IntrospectRequest } from '../types/IntrospectRequest'
import { useAlertContext } from '../contexts/alert-context'
import { useHistoryContext } from '../contexts/history-context'
import { getFromStorage, removeFromStorage, setToStorage } from '../utils/storageUtils'

const Login = () => {
  const { userReducer } = useUserContext()
  const navigate = useNavigate()
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const entraIdLoginInProgress = useRef<any>(false)
  const { alert } = useAlertContext()
  const { historyReducer } = useHistoryContext()

  useEffect(() => {
    // If page is loaded with Entra Id response code in url: continue logging in
    const params = new URLSearchParams(window.location.search)
    const authorizationCode = params.get('code')
    if (authorizationCode && !entraIdLoginInProgress.current) {
      entraIdLoginInProgress.current = true
      const loginEntraId = async () => {
        setLoading(true)
        const oAuthPathData = getFromStorage<AuthorizeResponse>(OAUTH_PATH_DATA_KEY, true)
        if (!oAuthPathData) {
          alert.error('OAuthPathData missing')
          return
        }

        // Exchange code for Curity token
        const oAuthData = oAuthPathData
        const tokenData = { authorizationCode, redirectUri: getOAuthRedirectUri(), clientId: oAuthData.clientId }
        const tokenResponse = await post(`${config.ENTRA_ID_LOGIN_URL}/v1/token`, tokenData) as TokenResponse
        setLoading(false)
        entraIdLoginInProgress.current = false

        if (!tokenResponse?.accessToken) {
          alert.error('Bad tokenResponse')
          return
        }

        // Introspect        
        const headers = { 'x-client-name': ENTRA_ID_CLIENT }
        const body: IntrospectRequest = { token: tokenResponse.accessToken }
        const introspectResponse = await post<IntrospectRequest, IntrospectResponse>(`${config.ENTRA_ID_LOGIN_URL}/v1/introspect`, body, { headers })

        if (!introspectResponse?.jwt) {
          alert.error('Bad introspectResponse')
          return
        }

        setToStorage(AUTH_KEY, introspectResponse.jwt)
        setToStorage(AUTH_TYPE_KEY, AuthType[AuthType.EntraId])
        updateDefaultHeader('Authorization', `Bearer ${introspectResponse.jwt}`)
        userReducer.setUser(getEntraIdUserFromToken(introspectResponse.jwt))
        removeFromStorage(OAUTH_PATH_DATA_KEY)
        handleLoginCompleted()
      }
      loginEntraId()
    }
  }, [])

  const submitForm = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    try {
      setLoading(true)
      const response = await post(`${config.LOGIN_URL}`, { userName, password, scope: 300 }) as UserResponse
      setToStorage(AUTH_KEY, response.token)
      setToStorage(AUTH_TYPE_KEY, AuthType[AuthType.Identity])
      updateDefaultHeader('Authorization', `Bearer ${response.token}`)
      userReducer.setUser(response)
      handleLoginCompleted()
    } catch {
      setLoading(false)
      alert.error('Wrong username or password')
    }
  }

  const handleLoginCompleted = () => {
    historyReducer.pruneViewedSessions()
    navigate('/overview')
  }

  const handleOauthLogin = async function onLoginOauth(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    setLoading(true)

    const response = await get<AuthorizeResponse>(`${config.ENTRA_ID_LOGIN_URL}/v1/authorize?redirectUri=${getOAuthRedirectUri()}`)
    if (!response?.oAuthUri) {
      return
    }

    setToStorage(OAUTH_PATH_DATA_KEY, response)
    setLoading(false)
    window.location.href = response.oAuthUri
  }

  const getOAuthRedirectUri = function (): string {
    return `${window.location.protocol}//${window.location.host}/login`
  }

  return (
    <div className='login'>
      <div>
        <h1>ICA Now Dashboard</h1>
      </div>
      <div>
        <Card>
          <>
            <h2>Logga in</h2>
            <form onSubmit={submitForm} id="login-form">
              <InputText name="username" id="username" placeholder='Användarnamn' value={userName} ariaLabel="username-input" autoComplete='username' onChange={e => setUserName(e.target.value)} />
              <InputText className='mt-4' name="password" id="password" placeholder='Lösenord' type="password" value={password} ariaLabel="password-input" autoComplete='current-password' onChange={e => setPassword(e.target.value)} />
              <br />
              <button type="submit" className='button button-large w-full' data-testid='btn-identity-login'>Logga in </button>
            </form>
            <hr className='my-5'></hr>
            <button onClick={handleOauthLogin} className='button button-large'>
              Logga in med ditt MinButik-inlogg
            </button>
          </>
        </Card>
        <div className="grid place-content-center">
          {loading && <SpinnerIcon />}
        </div>
      </div>
    </div>
  )
}

export default Login